import React, { useEffect, useState } from 'react';

import './MotherDayPage.css';
import { useOrderModal, type OrderableProduct } from './components/OrderModalProvider';

type Product = OrderableProduct & {
  description: string;
  price: string;
  image: string;
  imageAlt: string;
};

const WHATSAPP_NUMBER = '5977130133';
const WHATSAPP_BASE_URL = `https://wa.me/${WHATSAPP_NUMBER}?text=`;

const getOptimizedImage = (image: string, width: 480 | 720 | 1200) =>
  image.replace('/img/', '/img/optimized/').replace(/\.jpe?g$/i, `-${width}.jpg`);

const getProductSrcSet = (image: string) =>
  `${getOptimizedImage(image, 480)} 480w, ${getOptimizedImage(image, 720)} 720w, ${getOptimizedImage(image, 1200)} 1200w`;

const baskets: Product[] = [
  {
    code: 'MP_FB01',
    name: 'Blush Garden Basket',
    description: 'A luxurious basket arrangement with roses, lilies and seasonal blooms in soft pink tones. Full, elegant and perfect for a lasting impression.',
    price: 'SRD 5500',
    image: '/img/MP_FB01.jpeg',
    imageAlt: 'Blush Garden Basket with roses and lilies',
  },
  {
    code: 'MP_FB03',
    name: 'Pink Whisper Basket',
    description: 'A charming basket arrangement with roses, gerberas and seasonal blooms in soft pink tones. Fresh, elegant and full of warmth.',
    price: 'SRD 3850',
    image: '/img/MP_FB03.jpeg',
    imageAlt: 'Pink Whisper Basket with roses and gerberas',
  },
  {
    code: 'MP_FB04',
    name: 'Classic Grace Box',
    description: 'An elegant arrangement of lilies, roses and fresh greenery in a modern hat box. Timeless, refined and beautifully understated.',
    price: 'SRD 3000',
    image: '/img/MP_FB04.jpeg',
    imageAlt: 'Classic Grace Box with lilies and roses',
  },
  {
    code: 'MP_FB05',
    name: 'Mom Bloom Basket',
    description: 'A charming basket with roses, gerberas and fresh greenery. Light, cheerful and perfect for a thoughtful Mother\'s Day surprise.',
    price: 'SRD 2500',
    image: '/img/MP_FB05.jpeg',
    imageAlt: 'Mom Bloom Basket with roses and gerberas',
  },
  {
    code: 'MP_FB06',
    name: 'Sweet Harmony Basket',
    description: 'A luxurious arrangement of premium red roses paired with a teddy bear and Ferrero Rocher chocolates. Rich, romantic and designed to make a lasting impression.',
    price: '€275',
    image: '/img/MP_FB06.jpeg',
    imageAlt: 'Sweet Harmony Basket with red roses, teddy bear and chocolates',
  },
  {
    code: 'MP_FB07',
    name: 'Timeless Love Basket',
    description: 'A timeless arrangement of red roses paired with a teddy bear. Romantic, elegant and perfect to express love and appreciation.',
    price: '€165',
    image: '/img/MP_FB07.jpeg',
    imageAlt: 'Timeless Love Basket with red roses and teddy bear',
  },
  {
    code: 'MP_FB08',
    name: 'Delicate Charm Basket',
    description: 'A charming arrangement of roses with Ferrero Rocher chocolates and a teddy bear. Sweet, thoughtful and perfect for a heartfelt surprise.',
    price: '€140',
    image: '/img/MP_FB08.jpeg',
    imageAlt: 'Delicate Charm Basket with roses, chocolates and teddy bear',
  },
  {
    code: 'MP_FB09',
    name: 'Pure Beauty Basket',
    description: 'A refined arrangement of premium pink roses paired with a teddy bear. Soft, elegant and perfect for a heartfelt surprise.',
    price: '€190',
    image: '/img/MP_FB09.jpeg',
    imageAlt: 'Pure Beauty Basket with pink roses and teddy bear',
  },
];

const bouquets: Product[] = [
  {
    code: 'MP_BQ01',
    name: 'Blush Elegance Bouquet',
    description: 'A romantic bouquet of soft pink roses and delicate baby\'s breath. Designed to express love, appreciation and elegance.',
    price: 'SRD 6500',
    image: '/img/MP_BQ01.jpeg',
    imageAlt: 'Blush Elegance Bouquet with pink roses and baby\'s breath',
  },
  {
    code: 'MP_BQ03',
    name: 'Pink Luxury Bouquet',
    description: 'A refined blend of roses, lilies and seasonal blooms in soft pink tones. Elegant, feminine and designed to impress.',
    price: 'SRD 5000',
    image: '/img/MP_BQ03.jpeg',
    imageAlt: 'Pink Luxury Bouquet with roses and lilies',
  },
  {
    code: 'MP_BQ04',
    name: 'Elegant Moments Bouquet',
    description: 'A vibrant mix of sunflowers and seasonal blooms. Bright, joyful and perfect to celebrate Mom.',
    price: 'SRD 3500',
    image: '/img/MP_BQ04.jpeg',
    imageAlt: 'Elegant Moments Bouquet with sunflowers',
  },
  {
    code: 'MP_BQ05',
    name: 'Graceful Bloom Bouquet',
    description: 'A fresh and vibrant mix of alstroemeria, roses and seasonal greens. Light, cheerful and perfect for a thoughtful gesture.',
    price: 'SRD 2500',
    image: '/img/MP_BQ05.jpeg',
    imageAlt: 'Graceful Bloom Bouquet with alstroemeria and roses',
  },
  {
    code: 'MP_BQ06',
    name: 'Sweet Love Bouquet',
    description: 'A classic bouquet of premium red roses. Elegant, romantic and the perfect way to express love and appreciation.',
    price: 'SRD 4500',
    image: '/img/MP_BQ06.jpeg',
    imageAlt: 'Sweet Love Bouquet with premium red roses',
  },
  {
    code: 'MP_BQ07',
    name: 'Timeless Beauty Bouquet',
    description: 'A classic arrangement of roses, lilies and fresh greenery. Simple, elegant and always appreciated.',
    price: 'SRD 3000',
    image: '/img/MP_BQ07.jpeg',
    imageAlt: 'Timeless Beauty Bouquet with roses and lilies',
  },
  {
    code: 'MP_BQ08',
    name: 'Modern Mom Bouquet',
    description: 'A fresh and stylish mix of sunflowers, roses and delicate fillers. Bright, warm and perfect for the modern Mom.',
    price: 'SRD 4500',
    image: '/img/MP_BQ08.jpeg',
    imageAlt: 'Modern Mom Bouquet with sunflowers and roses',
  },
  {
    code: 'MP_BQ09',
    name: 'Signature Luxury Bouquet',
    description: 'A refined bouquet of elegant blooms, carefully arranged to create a timeless and luxurious look. Designed to impress and make every moment feel special.',
    price: 'SRD 4000',
    image: '/img/MP_BQ09.jpeg',
    imageAlt: 'Signature Luxury Bouquet with elegant blooms',
  },
];

const miniGardens: Product[] = [
  {
    code: 'MP_MG01',
    name: 'Forest Cascade',
    description: 'A unique mini garden inspired by nature\'s layers. Featuring a mix of succulents and greenery arranged in a natural cascading design, a modern, long-lasting gift.',
    price: '€90',
    image: '/img/MP_MG01.jpeg',
    imageAlt: 'Forest Cascade mini garden with succulents',
  },
  {
    code: 'MP_MG02',
    name: 'Serenity Garden',
    description: 'A calming mini garden with a harmonious blend of succulents and natural textures. Designed to bring balance, simplicity and a touch of greenery into any space.',
    price: '€75',
    image: '/img/MP_MG02.jpeg',
    imageAlt: 'Serenity Garden mini garden with succulents',
  },
  {
    code: 'MP_MG04',
    name: 'Wild Bloom Garden',
    description: 'A lively mini garden with a playful mix of succulents and natural textures. Its layered design and organic shapes create a vibrant, eye-catching piece for any space.',
    price: '€65',
    image: '/img/MP_MG04.jpeg',
    imageAlt: 'Wild Bloom Garden mini garden with succulents',
  },
  {
    code: 'MP_MG05',
    name: 'Pure Balance',
    description: 'A refined mini garden that blends structure, texture and natural elements in perfect harmony. Designed to create a sense of balance and understated luxury in any space.',
    price: '€90',
    image: '/img/MP_MG05.jpeg',
    imageAlt: 'Pure Balance mini garden with succulents',
  },
];

const openWhatsApp = (message: string) => {
  window.open(`${WHATSAPP_BASE_URL}${encodeURIComponent(message)}`, '_blank', 'noopener,noreferrer');
};

const getMoreInfoMessage = (product: Product) =>
  `Hi, I would like more information about product ${product.code} - ${product.name}.`;

const getGeneralOrderMessage = () =>
  'Hi, I would like to order a Mother\'s Day arrangement. Can you help me?';

const getCustomMessage = () =>
  'Hi, I would like to request a custom phalaenopsis or floral arrangement.';

const ProductCard: React.FC<{
  product: Product;
  onOrder: (product: Product) => void;
  onImageZoom: (product: Product) => void;
}> = ({ product, onOrder, onImageZoom }) => {
  return (
    <article className="md-card">
      <button
        type="button"
        className="md-card-media"
        onClick={() => onImageZoom(product)}
        aria-label={`View larger image of ${product.name}`}
      >
        <img
          src={getOptimizedImage(product.image, 720)}
          srcSet={getProductSrcSet(product.image)}
          sizes="(min-width: 980px) 360px, (min-width: 720px) 50vw, 50vw"
          alt={product.imageAlt}
          loading="lazy"
          decoding="async"
        />
        <span className="md-card-code">{product.code}</span>
      </button>
      <div className="md-card-body">
        <h3>{product.name}</h3>
        <p>{product.description}</p>
        <div className="md-card-meta">
          <span className="md-card-price">{product.price}</span>
          <span className="md-card-code-inline">{product.code}</span>
        </div>
        <div className="md-card-actions">
          <button
            type="button"
            className="md-button md-button-secondary"
            onClick={() => openWhatsApp(getMoreInfoMessage(product))}
          >
            More info
          </button>
          <button
            type="button"
            className="md-button md-button-primary"
            onClick={() => onOrder(product)}
          >
            Order now
          </button>
        </div>
      </div>
    </article>
  );
};

const ProductSection: React.FC<{
  id: string;
  eyebrow: string;
  title: string;
  intro: string;
  extra?: string;
  products: Product[];
  onOrder: (product: Product) => void;
  onImageZoom: (product: Product) => void;
}> = ({ id, eyebrow, title, intro, extra, products, onOrder, onImageZoom }) => {
  return (
    <section id={id} className="md-section">
      <div className="md-section-heading">
        <p className="md-eyebrow">{eyebrow}</p>
        <h2>{title}</h2>
        <p className="md-intro">{intro}</p>
        {extra ? <p className="md-extra">{extra}</p> : null}
      </div>
      <div className="md-grid">
        {products.map((product) => (
          <ProductCard
            key={product.code}
            product={product}
            onOrder={onOrder}
            onImageZoom={onImageZoom}
          />
        ))}
      </div>
    </section>
  );
};

const ImageZoomModal: React.FC<{
  product: Product;
  onClose: () => void;
}> = ({ product, onClose }) => {
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  return (
    <div className="md-image-modal-backdrop" onClick={onClose} role="presentation">
      <div
        className="md-image-modal"
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={product.name}
      >
        <button
          type="button"
          className="md-image-modal-close"
          onClick={onClose}
          aria-label="Close image preview"
        >
          <i className="fas fa-times" aria-hidden="true" />
        </button>
        <img
          src={getOptimizedImage(product.image, 1200)}
          srcSet={getProductSrcSet(product.image)}
          sizes="min(100vw, 42rem)"
          alt={product.imageAlt}
          decoding="async"
        />
        <div className="md-image-modal-caption">
          <strong>{product.name}</strong>
          <span>{product.code}</span>
        </div>
      </div>
    </div>
  );
};

const MotherDayPage: React.FC = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [zoomedProduct, setZoomedProduct] = useState<Product | null>(null);
  const { openOrderModal, isOrderModalOpen } = useOrderModal();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    document.body.style.overflow = isOrderModalOpen || zoomedProduct ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOrderModalOpen, zoomedProduct]);

  const closeMenu = () => setMenuOpen(false);

  return (
    <div className="md-page">
      <a
        className="md-sticky-whatsapp"
        href={`${WHATSAPP_BASE_URL}${encodeURIComponent(getGeneralOrderMessage())}`}
        target="_blank"
        rel="noreferrer"
        aria-label="Order via WhatsApp"
      >
        <i className="fab fa-whatsapp" aria-hidden="true" />
      </a>

      <header className="md-header">
        <a href="#top" className="md-brand" onClick={closeMenu}>
          <img src="/img/monplaisir-logo.png" alt="" aria-hidden="true" />
          <span>Mon Plaisir Nursery</span>
        </a>
        <button
          type="button"
          className={`md-menu-toggle ${menuOpen ? 'is-open' : ''}`}
          onClick={() => setMenuOpen((open) => !open)}
          aria-label="Toggle navigation"
          aria-expanded={menuOpen}
        >
          <span />
          <span />
          <span />
        </button>
        <nav className={`md-nav ${menuOpen ? 'is-open' : ''}`}>
          <a href="#baskets" onClick={closeMenu}>Baskets</a>
          <a href="#bouquets" onClick={closeMenu}>Bouquets</a>
          <a href="#mini-gardens" onClick={closeMenu}>Mini Gardens</a>
          <a href="#custom" onClick={closeMenu}>Custom</a>
          <a href="#how-to-order" onClick={closeMenu}>Hoe bestel je?</a>
        </nav>
      </header>

      <main id="top">
        <section className="md-hero">
          <div className="md-hero-image-wrap">
            <img
              className="md-hero-image"
              src={getOptimizedImage('/img/MP_FB01.jpeg', 1200)}
              srcSet={getProductSrcSet('/img/MP_FB01.jpeg')}
              sizes="(min-width: 980px) 700px, 100vw"
              alt="Premium Mother's Day flower arrangement"
              decoding="async"
            />
          </div>
          <div className="md-hero-panel">
            <img
              className="md-hero-logo"
              src="/img/monplaisir-logo.png"
              alt="Mon Plaisir Nursery"
            />
            <p className="md-eyebrow">Mother's Day Collection</p>
            <h1>Celebrate Mother's Day with Timeless Beauty</h1>
            <p className="md-hero-copy">
              Exclusive flowers, elegant arrangements, and unforgettable gifts for the
              most important woman in your life.
            </p>
            <div className="md-hero-info" aria-label="Contact and opening hours">
              <a
                href="https://www.google.com/maps/search/?api=1&query=Siemensstraat%2019%2C%20Paramaribo"
                target="_blank"
                rel="noreferrer"
              >
                <i className="fas fa-map-marker-alt" aria-hidden="true" />
                <span>Siemensstraat 19, Paramaribo</span>
              </a>
              <a href="tel:551313">
                <i className="fas fa-phone-alt" aria-hidden="true" />
                <span>Tel: 551313</span>
              </a>
              <a
                href={`${WHATSAPP_BASE_URL}${encodeURIComponent(getGeneralOrderMessage())}`}
                target="_blank"
                rel="noreferrer"
              >
                <i className="fab fa-whatsapp" aria-hidden="true" />
                <span>WhatsApp: 7130133</span>
              </a>
              <div>
                <i className="far fa-clock" aria-hidden="true" />
                <span>
                  <strong>Speciale openingstijden</strong><br />
                  Friday 9am - 7pm<br />
                  Saturday 9am - 7pm<br />
                  Sunday 9am - 3pm
                </span>
              </div>
            </div>
            <p className="md-hero-note">Limited availability - while stock lasts</p>
          </div>
        </section>

        <ProductSection
          id="baskets"
          eyebrow="Mother's Day Collection"
          title="Flower Arrangements in Baskets"
          intro="Elegant basket arrangements designed to impress and last."
          products={baskets}
          onOrder={openOrderModal}
          onImageZoom={setZoomedProduct}
        />

        <ProductSection
          id="bouquets"
          eyebrow="Premium Florals"
          title="Luxury Bouquets"
          intro="Hand-tied bouquets crafted with premium imported flowers."
          products={bouquets}
          onOrder={openOrderModal}
          onImageZoom={setZoomedProduct}
        />

        <ProductSection
          id="mini-gardens"
          eyebrow="Living Gifts"
          title="Mini Gardens"
          intro="Elegant miniature gardens – natural, stylish and long-lasting."
          extra="Custom mini gardens available (order before May 6)"
          products={miniGardens}
          onOrder={openOrderModal}
          onImageZoom={setZoomedProduct}
        />

        <section id="custom" className="md-section md-custom">
          <div className="md-custom-media">
            <img
              src={getOptimizedImage('/img/MP_MG01.jpeg', 1200)}
              srcSet={getProductSrcSet('/img/MP_MG01.jpeg')}
              sizes="(min-width: 980px) 640px, 100vw"
              alt="Custom mini garden design"
              loading="lazy"
              decoding="async"
            />
          </div>
          <div className="md-custom-body">
            <p className="md-eyebrow">Bespoke Floral Design</p>
            <h2>Custom Phalaenopsis & Floral Designs</h2>
            <p className="md-intro">
              Looking for something truly unique? We create custom phalaenopsis
              arrangements and floral designs tailored to your wishes.
            </p>
            <a
              className="md-button md-button-primary"
              href={`${WHATSAPP_BASE_URL}${encodeURIComponent(getCustomMessage())}`}
              target="_blank"
              rel="noreferrer"
            >
              Request a custom arrangement via WhatsApp
            </a>
          </div>
        </section>

        <section id="how-to-order" className="md-section md-order-section">
          <div className="md-section-heading">
            <p className="md-eyebrow">Bestelinstructies</p>
            <h2>Hoe bestel je?</h2>
          </div>
          <div className="md-steps">
            <article className="md-step">
              <span>1</span>
              <div>
                <h3>Kies je favoriete product</h3>
                <p>Selecteer het arrangement dat het beste past.</p>
              </div>
            </article>
            <article className="md-step">
              <span>2</span>
              <div>
                <h3>Klik op "Order now"</h3>
                <p>Vul je bestelgegevens in het venster op de pagina in.</p>
              </div>
            </article>
            <article className="md-step">
              <span>3</span>
              <div>
                <h3>Verstuur je aanvraag via WhatsApp</h3>
                <p>De ingevulde gegevens worden automatisch in je bericht gezet.</p>
              </div>
            </article>
            <article className="md-step">
              <span>4</span>
              <div>
                <h3>Wij bevestigen je bestelling en levering</h3>
                <p>Je ontvangt een bevestiging zodra alles is gecontroleerd.</p>
              </div>
            </article>
          </div>
        </section>

        <section className="md-section md-info-section">
          <div className="md-section-heading">
            <p className="md-eyebrow">Delivery & Information</p>
            <h2>Delivery & Information</h2>
          </div>
          <div className="md-info-grid">
            <article>
              <h3>Levering of afhalen mogelijk</h3>
              <p>Kies in WhatsApp of je bestelling geleverd wordt of wordt afgehaald.</p>
            </article>
            <article>
              <h3>Datum naar keuze</h3>
              <p>De klant kiest zelf de gewenste datum voor levering of pickup.</p>
            </article>
            <article>
              <h3>Leveringskosten achteraf bevestigd</h3>
              <p>De bezorgkosten worden bevestigd voor de order definitief wordt goedgekeurd.</p>
            </article>
            <article>
              <h3>Zolang de voorraad strekt</h3>
              <p>Beschikbaarheid is beperkt tijdens Moederdag en afhankelijk van voorraad.</p>
            </article>
          </div>
        </section>

        <section className="md-section md-closing">
          <div className="md-closing-card">
            <p className="md-closing-text">Because she deserves something truly special.</p>
            <a
              className="md-button md-button-primary"
              href={`${WHATSAPP_BASE_URL}${encodeURIComponent(getGeneralOrderMessage())}`}
              target="_blank"
              rel="noreferrer"
            >
              Order via WhatsApp
            </a>
          </div>
        </section>
      </main>

      {zoomedProduct ? (
        <ImageZoomModal product={zoomedProduct} onClose={() => setZoomedProduct(null)} />
      ) : null}
    </div>
  );
};

export default MotherDayPage;
